import { Gift, Mail } from 'lucide-react';
import { Avatar } from '../profiles/avatar';
import { useInviteLinkPreviewQuery } from './api';

/**
 * Who an invite link leads to, above the sign-in form (and again above the
 * handle, once signed in). Deliberately quiet: a handle, a name, a seal —
 * nothing about who else they write to.
 */
export function InviteLinkNote({
  token,
  signedIn = false,
}: {
  token: string;
  /** On the handle screen: the account exists, so the next step is a handle, not a sign-in. */
  signedIn?: boolean;
}) {
  const { data, isLoading, isError } = useInviteLinkPreviewQuery(token);

  if (isError) {
    return (
      <div className="invite-note" role="status">
        <Mail size={16} aria-hidden="true" className="invite-note__icon" />
        <div>
          <h2 className="invite-note__title">This invite link has closed</h2>
          <p className="invite-note__body">
            Links stay open for a week and for a few people. Ask whoever sent it for a fresh one,
            or join anyway and find them by their @handle.
          </p>
        </div>
      </div>
    );
  }

  // A lasting link (QR, story, bio) that has started all its new pairs today.
  if (data?.fullToday) {
    return (
      <div className="invite-note" role="status">
        <Mail size={16} aria-hidden="true" className="invite-note__icon" />
        <div>
          <h2 className="invite-note__title">Lots of new post for @{data.handle} today</h2>
          <p className="invite-note__body">
            Their letterbox is taking a breather. Try again tomorrow, or join anyway and find them
            by their @handle: <strong>@{data.handle}</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-note" role="status">
      {data ? (
        <span className="invite-note__avatar">
          <Avatar name={data.displayName ?? data.handle} avatar={data.avatar} />
        </span>
      ) : (
        <Mail size={16} aria-hidden="true" className="invite-note__icon" />
      )}
      <div>
        <h2 className="invite-note__title">
          {isLoading || !data ? (
            'Checking the link…'
          ) : data.gift ? (
            <>@{data.handle} has sent you a stamp book</>
          ) : (
            <>@{data.handle} would like to hear from you</>
          )}
        </h2>
        {data?.gift && (
          <p className="invite-note__gift">
            <Gift size={14} aria-hidden="true" /> {data.gift.collectionName}, with a note from them
          </p>
        )}
        <p className="invite-note__body">
          {signedIn ? (
            <>
              You&apos;re nearly there. Choose your toiyp below, and a blank page addressed to{' '}
              {data ? <strong>@{data.handle}</strong> : 'them'} will be waiting
              {data?.gift ? ', with the stamp book already in your stamp book' : ''}. Nothing is sent
              until you write it.
            </>
          ) : (
            <>
              toiyp is for letters, written slowly. Create an account or sign in, and a blank page
              addressed to {data ? <strong>@{data.handle}</strong> : 'them'} will be waiting
              {data?.gift ? ', and the stamp book will be in your stamp book' : ''}. Nothing is sent
              until you write it.
            </>
          )}
        </p>
      </div>
    </div>
  );
}
